import type { Trip } from '../../api/types'
import { useZonenverlauf } from '../../hooks/queries'
import { useOnline } from '../../hooks/useOutbox'
import { usePreference } from '../../lib/preference'
import { gleicheZone, stadtVon, zoneBei, zoneDesGeraets } from '../../lib/zonen'
import { useTexte } from '../../texte'

/**
 * The suggestion when the author's device is in another zone than the
 * trip's course says for now (1.87.0): "Du bist jetzt in New York. Ab jetzt
 * für die Reise übernehmen?" -- one tap, and the course has a new entry
 * from this moment.
 *
 * **Compared by name, not by offset**: Berlin and Paris have the same
 * offset and are two zones, and Berlin in October has two offsets and is
 * one.
 *
 * **A "Nein" holds until the next day** (1.103.0), per trip and zone on
 * this device, so the line does not come back at every opening. Until here
 * it held for good. From use: "in einer Reise ist New York eingestellt …
 * ich bin aber in Deutschland. es kommt aber beim Aufruf der Reise keine
 * Abfrage mehr" -- one "Nein" at home had ended the question for that zone
 * for the rest of the trip, also on the day the author was there again
 * and wanted it. Decided from use: ask again the next calendar day of the
 * device. A "Nein" stored in the old form (a list of zones) counts as
 * yesterday's.
 *
 * Only with a net: the entry is the server's, and a suggestion that cannot
 * be taken would be a button that does nothing.
 */
export function Zonenvorschlag({ trip }: { trip: Trip }) {
  const t = useTexte().chat.zonenvorschlag
  const online = useOnline()
  const verlauf = useZonenverlauf(trip.id)
  const geraet = zoneDesGeraets()
  const [abgelehnt, setAbgelehnt] = usePreference<Record<string, string> | string[]>(`tb-zonenvorschlag-${trip.id}`, {})
  const jetzt = zoneBei(trip.zones, Date.now())
  const today = deviceDay()
  const declined = Array.isArray(abgelehnt) ? {} : abgelehnt

  if (!online || trip.archived_at !== null) return null
  if (gleicheZone(jetzt, geraet) || declined[geraet] === today) return null

  return (
    <div className="notice notice--info notice--rand zonenvorschlag" role="status" data-schluessel="zonenvorschlag" data-zone={geraet}>
      <span>{t.frage(stadtVon(geraet))}</span>
      <span className="row">
        <button
          type="button"
          className="button button--klein"
          disabled={verlauf.anlegen.isPending}
          onClick={() => verlauf.anlegen.mutate({ tz_name: geraet })}
        >
          {t.uebernehmen}
        </button>
        <button
          type="button"
          className="button button--klein button--outline"
          onClick={() => setAbgelehnt({ ...declined, [geraet]: today })}
        >
          {t.nein}
        </button>
      </span>
    </div>
  )
}

/** Today in the device's clock, as `YYYY-MM-DD` -- the day a "Nein" holds. */
function deviceDay(): string {
  const now = new Date()
  const two = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${two(now.getMonth() + 1)}-${two(now.getDate())}`
}
